import { useMemo } from 'react';
import { API_ENDPOINT_GROUPS, apiDocsText } from '@shared/apiDocs.ts';
import { functionsUrl } from '@/lib/supabase.ts';
import { navigate } from '@/lib/router.ts';
import { Page, Card } from './Page.tsx';
import { Button, SectionLabel, useToast } from '@/components/ui.tsx';

/**
 * The public API reference — and, like `/protocol`, a page that does not need
 * a session. A script deciding whether to integrate has no key yet to read
 * this with, so it has to be reachable before one exists.
 *
 * Rendered from `API_ENDPOINT_GROUPS`, the same list `GET /api/docs` and
 * `npm run api-docs` render from, so this cannot describe an API the server
 * does not actually have.
 */
export function ApiDocsScreen({ standalone }: { standalone?: boolean }) {
  const content = <ApiDocsContent />;
  if (!standalone) return content;

  return (
    <div className="min-h-screen min-h-dvh bg-app">
      <PublicHeader />
      {content}
    </div>
  );
}

function PublicHeader() {
  return (
    <div className="flex items-center gap-2.5 border-b border-line px-4 py-3 sm:px-6 lg:px-8">
      <button
        onClick={() => navigate('/')}
        className="rg-focus flex min-w-0 items-center gap-2.5 rounded px-1.5 text-left"
      >
        <span className="size-[20px] shrink-0 rounded-md bg-accent" aria-hidden />
        <span className="text-[15px] font-bold tracking-[-0.01em] text-hi">Rigg</span>
      </button>
      <span className="flex-1" />
      <button
        onClick={() => navigate('/')}
        className="rg-focus rounded px-2 py-1 text-[13px] font-semibold text-mute hover:text-hi2"
      >
        Sign in
      </button>
    </div>
  );
}

function ApiDocsContent() {
  const { notify } = useToast();

  const origin = useMemo(
    () => (typeof window === 'undefined' ? '' : window.location.origin.replace(/\/+$/, '')),
    [],
  );
  const appUrl = origin || '<your rigg app url>';
  const base = `${functionsUrl}/api`;

  const text = useMemo(() => apiDocsText({ functionsUrl, appUrl }), [appUrl]);

  const copy = async (value: string, what: string) => {
    try {
      await navigator.clipboard.writeText(value);
      notify(`${what} copied`);
    } catch {
      notify('Could not copy');
    }
  };

  return (
    <Page
      title="API docs"
      wide
      blurb={
        <>
          Every write the app can make, and the reads a script needs to work without a browser
          session — public, no sign-in needed. Provision a key from{' '}
          <button
            onClick={() => navigate('/settings')}
            className="rg-focus font-semibold text-accent underline-offset-2 hover:underline"
          >
            Settings
          </button>{' '}
          to actually call it.
        </>
      }
      actions={
        <Button tone="primary" onClick={() => void copy(text, 'The whole reference')}>
          Copy as text
        </Button>
      }
    >
      <SectionLabel className="mb-2.5">Authentication</SectionLabel>
      <Card className="mb-6">
        <p className="text-[12.5px] leading-relaxed text-mute">
          Every call carries <code className="font-mono text-t7">Authorization: Bearer &lt;token&gt;</code>.
          A Supabase session (what the browser holds) and a personal{' '}
          <strong className="text-t2">API key</strong> (<code className="font-mono text-t7">rgk_…</code>,
          minted from Settings) resolve to exactly the same thing — a profile and a role per
          project — so a key can do anything your own sign-in can, for the projects you belong to,
          and nothing else. Only the SHA-256 of a key is stored: a lost one is replaced, never
          recovered.
        </p>
      </Card>

      <SectionLabel className="mb-2.5">Submitting a task from a script</SectionLabel>
      <Card className="mb-6">
        <pre className="overflow-x-auto rounded-xl border border-line bg-brief p-3.5 font-mono text-[12px] leading-relaxed whitespace-pre-wrap text-t6">
          {`curl -X POST ${base}/tasks \\\n  -H "Authorization: Bearer $RIGG_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "project_id": "<project id>",\n    "prompt": "The lockfile is 3 months stale — bump it.",\n    "category": "Maintenance"\n  }'`}
        </pre>
      </Card>

      <SectionLabel className="mb-2.5">The endpoints</SectionLabel>
      {API_ENDPOINT_GROUPS.map((group) => (
        <Card key={group.title} className="mb-4">
          <div className="mb-2 text-[13px] font-[650] text-hi2">{group.title}</div>
          {group.blurb ? <p className="mb-2.5 text-[12px] text-mute">{group.blurb}</p> : null}
          <div className="flex flex-col gap-1.5 font-mono text-[12px]">
            {group.endpoints.map((endpoint) => (
              <Endpoint key={`${endpoint.method} ${endpoint.path}`} {...endpoint} />
            ))}
          </div>
        </Card>
      ))}

      <p className="mt-2 mb-6 text-[12.5px] leading-relaxed text-mute">
        Base URL <code className="font-mono text-t7">{base}</code>. What a routine itself needs —
        reporting, polling, the callback protocol — is a different surface, published at{' '}
        <button onClick={() => navigate('/protocol')} className="text-accent hover:underline">
          /protocol
        </button>
        .
      </p>
    </Page>
  );
}

function Endpoint({ method, path, note }: { method: string; path: string; note: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="w-14 shrink-0 font-semibold text-accent">{method}</span>
      <span className="text-t1">{path}</span>
      <span className="font-sans text-[12px] text-mute">{note}</span>
    </div>
  );
}
